import classNames from 'classnames';
import { isFuture } from 'date-fns';
import type { ReactNode } from 'react';
import type { To } from 'react-router-dom';
import styled, { css } from 'styled-components';

import { Link } from '@sorare/routing';

import { So5FixtureEvent } from '__generated__/globalTypes';
import { Skeleton } from 'atoms/animations/Skeleton';
import { ConditionalWrapper } from 'atoms/layout/ConditionalWrapper';
import { Vertical } from 'atoms/layout/flex';
import { FixtureState } from 'lib/so5';
import { laptopAndAbove } from 'style/mediaQuery';

import { FixtureItemTag } from './FixtureItemTag';
import { FixturePickerContent } from './FixturePickerContent';

const StyledLink = styled(Link)<{ disabled?: boolean }>`
  pointer-events: ${({ disabled }) => (disabled ? 'none' : 'auto')};
  opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};
`;

const smallWrapperStyles = css`
  padding: 0 var(--half-unit);
  border-radius: var(--double-unit);
  aspect-ratio: 1;
  width: 67px;
  height: 67px;
  overflow: hidden;
`;

const bigWrapperStyles = css`
  width: 120px;
  height: 65px;
  overflow: hidden;
  border-radius: var(--double-unit);

  @media ${laptopAndAbove} {
    width: 140px;
    height: 76px;
  }
`;

const StyledSkeleton = styled(Skeleton)`
  /* default display: inline-block combining with height: 100%
  will create an extra height on the Skeleton and causes overflow-y */
  display: flex;

  &.small {
    ${smallWrapperStyles}
  }

  &.big {
    ${bigWrapperStyles}
  }
`;

const Wrapper = styled(Vertical).attrs({ gap: 0 })`
  cursor: pointer;
  background: var(--c-nd-100);
  &.opaque {
    background: var(--c-nd-100-opaque);
  }
  &.withTag {
    justify-content: flex-end;
  }
  &.selected {
    background: var(--c-white);
  }

  &.small {
    ${smallWrapperStyles}
  }

  &.big {
    ${bigWrapperStyles}
  }
`;

const Footer = styled.div`
  width: 100%;
  text-align: center;

  &.small {
    margin-bottom: var(--half-unit);
  }
`;

type Props = {
  fixtureState: Nullable<FixtureState>;
  selected?: boolean;
  eventType?: string;
  startDate?: Date;
  endDate?: Date;
  isUpcoming?: boolean;
  onClick?: () => void;
  loading?: boolean;
  to?: To;
  state?: Record<string, unknown>;
  disabled?: boolean;
  expectedRewardsDistributionDate?: Nullable<Date>;
  opaque?: boolean;
  variant?: 'small' | 'big';
  gameWeek?: string;
  pastFixtureFooter?: ReactNode;
};

export const FixturePickerItem = ({
  fixtureState,
  startDate,
  endDate,
  eventType,
  isUpcoming,
  selected,
  pastFixtureFooter,
  onClick,
  gameWeek,
  loading,
  to,
  state,
  disabled,
  expectedRewardsDistributionDate,
  opaque,
  variant = 'small',
}: Props) => {
  const isLive = fixtureState === FixtureState.started;
  const waitingForRewards =
    expectedRewardsDistributionDate &&
    fixtureState !== FixtureState.opened &&
    isFuture(expectedRewardsDistributionDate);
  const withTag = isLive || isUpcoming || waitingForRewards;

  if (loading) {
    return <StyledSkeleton className={variant} />;
  }

  return (
    <ConditionalWrapper
      wrap={!!to}
      Wrapper={StyledLink}
      props={{
        to: disabled ? '.' : to!,
        onClick,
        replace: true,
        state,
        disabled,
      }}
    >
      <Wrapper
        center
        className={classNames(variant, {
          withTag,
          selected,
          opaque,
        })}
      >
        <FixturePickerContent
          variant={variant}
          endDate={endDate}
          selected={selected}
          gameWeek={gameWeek}
          startDate={startDate}
          fixtureState={fixtureState}
          isDaily={eventType?.toUpperCase() === So5FixtureEvent.DAILY}
        />
        {!!withTag && (
          <Footer className={variant}>
            <FixtureItemTag
              isLive={isLive}
              isUpcoming={isUpcoming}
              startDate={startDate}
              waitingForRewards={waitingForRewards}
              expectedRewardsDistributionDate={expectedRewardsDistributionDate}
            />
          </Footer>
        )}
        {!withTag && !!pastFixtureFooter && (
          <Footer className="small">{pastFixtureFooter}</Footer>
        )}
      </Wrapper>
    </ConditionalWrapper>
  );
};
